import { zodResolver } from '@hookform/resolvers/zod';
import {
  CheckboxInput,
  DataGrid,
  Dialog,
  FilterToolbar,
  Form,
  FormButton,
  ImageUploader,
  Input,
  string,
  SwitchInput,
  useNotifications,
  type FilterDef,
  type GridQuery
} from '@ijoin/components';
import {
  GetAdminAdvisoryOrgAdvisorsParams,
  getGetAdminAdvisoryOrgAdvisorsQueryKey,
  UploadAssetBodyContentType,
  useDeleteAdminAdvisoryOrganizationAdvisor,
  useGetAdminAdvisoryOrgAdvisors,
  usePatchAdminAdvisoryOrganizationAdvisor,
  usePostAdminAdvisoryOrganizationAdvisor,
  useUploadAsset
} from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';

import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';
import { z } from 'zod';

import { assetUrl } from '../helpers';
import { ApiError } from '../types';
import { AdvisorActionsCell } from './AdvisorActionsCell';

type AdvisorFormValues = {
  canCreateEmailCampaigns: boolean;
  canEditEmailCampaigns: boolean;
  canInvestorView: boolean;
  canIraProspect: boolean;
  canRunReports: boolean;
  canViewSensitiveData: boolean;
  crdNumber: string;
  email: string;
  firstName: string;
  hasAllPlans: boolean;
  headShotUrl: string;
  isAdmin: boolean;
  isLocked: boolean;
  lastName: string;
  password: string;
  phone: string;
  username: string;
};

const defaultValues: AdvisorFormValues = {
  canCreateEmailCampaigns: false,
  canEditEmailCampaigns: false,
  canInvestorView: false,
  canIraProspect: false,
  canRunReports: false,
  canViewSensitiveData: false,
  crdNumber: '',
  email: '',
  firstName: '',
  hasAllPlans: false,
  headShotUrl: '',
  isAdmin: false,
  isLocked: false,
  lastName: '',
  password: '',
  phone: '',
  username: ''
};

const createResolver = zodResolver(
  z.object({
    canCreateEmailCampaigns: z.boolean(),
    canEditEmailCampaigns: z.boolean(),
    canInvestorView: z.boolean(),
    canIraProspect: z.boolean(),
    canRunReports: z.boolean(),
    canViewSensitiveData: z.boolean(),
    crdNumber: string().regex(/^\S*$/, 'general.validations.noWhitespace'),
    email: z.string().email(),
    firstName: string({ required: true }),
    hasAllPlans: z.boolean(),
    headShotUrl: string(),
    isAdmin: z.boolean(),
    isLocked: z.boolean(),
    lastName: string({ required: true }),
    password: string({ required: true }),
    phone: string(),
    username: string({ required: true })
  })
);

const editResolver = zodResolver(
  z.object({
    canCreateEmailCampaigns: z.boolean(),
    canEditEmailCampaigns: z.boolean(),
    canInvestorView: z.boolean(),
    canIraProspect: z.boolean(),
    canRunReports: z.boolean(),
    canViewSensitiveData: z.boolean(),
    crdNumber: string().regex(/^\S*$/, 'general.validations.noWhitespace'),
    email: z.string().email(),
    firstName: string({ required: true }),
    hasAllPlans: z.boolean(),
    headShotUrl: string(),
    isAdmin: z.boolean(),
    isLocked: z.boolean(),
    lastName: string({ required: true }),
    password: string(),
    phone: string(),
    username: string()
  })
);

const booleanFilterOptions = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' }
];

export const AdvisorsTab: FC<{ advisoryOrganizationId: string }> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const [advisorDialogOpen, toggleAdvisorDialogOpen, setAdvisorDialogOpen] =
    useToggle(false);
  const [editingAdvisorId, setEditingAdvisorId] = useState<null | string>(null);
  const [query, setQuery] = useState<GridQuery>();

  const filters: FilterDef[] = [
    { label: 'First Name', name: 'firstName', type: 'text' },
    { label: 'Last Name', name: 'lastName', type: 'text' },
    { label: 'Email', name: 'email', type: 'text' },
    {
      label: 'Admin',
      name: 'isAdmin',
      options: booleanFilterOptions,
      type: 'select'
    },
    {
      label: 'All Plans',
      name: 'hasAllPlans',
      options: booleanFilterOptions,
      type: 'select'
    }
  ];

  const advisors = useGetAdminAdvisoryOrgAdvisors(
    props.advisoryOrganizationId,
    query as GetAdminAdvisoryOrgAdvisorsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const methods = useForm<AdvisorFormValues>({
    defaultValues,
    mode: 'all',
    resolver: editingAdvisorId ? editResolver : createResolver
  });

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getGetAdminAdvisoryOrgAdvisorsQueryKey(
        props.advisoryOrganizationId
      )
    });
  };

  const deleteAdvisor = useDeleteAdminAdvisoryOrganizationAdvisor({
    mutation: {
      onError: () => notifications.error('Failed to delete advisor'),
      onSuccess: () => {
        notifications.success('Advisor deleted');
        invalidate();
      }
    }
  });

  const createAdvisor = usePostAdminAdvisoryOrganizationAdvisor<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: () => {
        methods.reset(defaultValues);
        setEditingAdvisorId(null);
        setAdvisorDialogOpen(false);
        invalidate();
        notifications.success('Advisor created');
      }
    }
  });

  const updateAdvisor = usePatchAdminAdvisoryOrganizationAdvisor<ApiError>({
    mutation: {
      onError: error => notifications.error(error.message),
      onSuccess: () => {
        methods.reset(defaultValues);
        setEditingAdvisorId(null);
        setAdvisorDialogOpen(false);
        invalidate();
        notifications.success('Advisor updated');
      }
    }
  });

  const uploadAsset = useUploadAsset({
    mutation: {
      onError: () => notifications.error('Failed to upload headshot')
    }
  });

  const headShotUrl = methods.watch('headShotUrl');

  const onSubmit = (data: AdvisorFormValues) => {
    const {
      canEditEmailCampaigns,
      isAdmin,
      isLocked,
      password,
      username,
      ...advisorFields
    } = data;

    const payload = {
      ...advisorFields,
      canEditEmailCampaigns: canEditEmailCampaigns ? 1 : 0
    };

    if (editingAdvisorId) {
      updateAdvisor.mutate({
        advisorId: editingAdvisorId,
        advisoryOrganizationId: props.advisoryOrganizationId,
        data: { ...payload, isAdmin, isLocked }
      });
    } else {
      createAdvisor.mutate({
        advisoryOrganizationId: props.advisoryOrganizationId,
        data: {
          ...payload,
          isAdmin,
          password,
          username
        }
      });
    }
  };

  const advisorColumns: GridColDef[] = [
    {
      field: 'firstName',
      flex: 1,
      headerName: 'First Name'
    },
    {
      field: 'lastName',
      flex: 1,
      headerName: 'Last Name'
    },
    {
      field: 'email',
      flex: 1.5,
      headerName: 'Email'
    },
    {
      field: 'isAdmin',
      flex: 0.5,
      headerName: 'Admin',
      renderCell: params => (params.value ? 'Yes' : 'No'),
      sortable: false
    },
    {
      field: 'hasAllPlans',
      flex: 0.5,
      headerName: 'All Plans',
      renderCell: params => (params.value ? 'Yes' : 'No'),
      sortable: false
    },
    {
      field: 'advisorId',
      flex: 1,
      headerName: 'Advisor ID',
      sortable: false
    },
    {
      align: 'center',
      field: 'actions',
      headerName: '',
      renderCell: params => {
        const row = params.row;
        return (
          <AdvisorActionsCell
            advisorId={row.advisorId}
            advisoryOrganizationId={props.advisoryOrganizationId}
            firstName={row.firstName}
            lastName={row.lastName}
            loginId={row.loginId}
            onDelete={() =>
              deleteAdvisor.mutate({
                advisorId: row.advisorId,
                advisoryOrganizationId: props.advisoryOrganizationId
              })
            }
            onEdit={() => {
              setEditingAdvisorId(row.advisorId);
              methods.reset({
                ...defaultValues,
                canCreateEmailCampaigns: Boolean(row.canCreateEmailCampaigns),
                canEditEmailCampaigns: Boolean(row.canEditEmailCampaigns),
                canInvestorView: Boolean(row.canInvestorView),
                canIraProspect: Boolean(row.canIraProspect),
                canRunReports: Boolean(row.canRunReports),
                canViewSensitiveData: Boolean(row.canViewSensitiveData),
                crdNumber: row.crdNumber ?? '',
                email: row.email ?? '',
                firstName: row.firstName ?? '',
                hasAllPlans: Boolean(row.hasAllPlans),
                headShotUrl: row.headShotUrl ?? '',
                isAdmin: Boolean(row.isAdmin),
                isLocked: Boolean(row.isLocked),
                lastName: row.lastName ?? '',
                phone: row.phone ?? ''
              });
              setAdvisorDialogOpen(true);
            }}
          />
        );
      },
      sortable: false,
      width: 65
    }
  ];

  const onCreateAdvisor = () => {
    setEditingAdvisorId(null);
    methods.reset(defaultValues);
    setAdvisorDialogOpen(true);
  };

  const onCloseDialog = toggleAdvisorDialogOpen;

  return (
    <>
      <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
        <DataGrid
          autoHeight={false}
          columns={advisorColumns}
          disableColumnMenu
          disableRowSelectionOnClick
          filterDefs={filters}
          getRowId={row => row.advisorId}
          initialSort={[{ field: 'lastName', sort: 'asc' }]}
          loading={!query || advisors.isFetching}
          onClear={setQuery}
          onSearch={setQuery}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode='server'
          rowCount={advisors.data?.pagination?.total ?? 0}
          rows={advisors.data?.data ?? []}
          showColumnMenu
          showToolbar
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'advisors'
            },
            toolbar: {
              addLabel: 'Advisor',
              addTestId: 'createAdvisor',
              onAdd: onCreateAdvisor,
              onRefresh: advisors.refetch,
              searchButtonTestId: 'advisorSearch',
              triggerTestId: 'advisorsFilterTrigger'
            }
          }}
          slots={{ toolbar: FilterToolbar }}
          sortingMode='server'
          sx={{ flex: 1, minHeight: 0 }}
        />
      </Stack>

      <Dialog
        data-testid='advisorDialog'
        isOpen={advisorDialogOpen}
        onClose={onCloseDialog}>
        <Form
          methods={methods}
          noValidate
          onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogTitle variant='h3'>
            {editingAdvisorId ? 'Edit Advisor' : 'Create Advisor'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Input
                data-testid='advisorFirstName'
                format='string'
                fullWidth
                hideError
                label='First Name'
                name='firstName'
                required
                size='small'
              />
              <Input
                data-testid='advisorLastName'
                format='string'
                fullWidth
                hideError
                label='Last Name'
                name='lastName'
                required
                size='small'
              />
              <Input
                data-testid='advisorEmail'
                format='string'
                fullWidth
                hideError
                label='Email'
                name='email'
                required
                size='small'
              />
              <Input
                data-testid='advisorPhone'
                format='string'
                fullWidth
                hideError
                label='Phone'
                name='phone'
                size='small'
              />
              <Input
                data-testid='advisorCrdNumber'
                format='string'
                fullWidth
                hideError
                label='Advisor CRD'
                name='crdNumber'
                size='small'
              />
              <ImageUploader
                alt='Headshot preview'
                data-testid='advisorHeadShotUrl'
                isUploading={uploadAsset.isPending}
                label='Upload headshot'
                onChange={key =>
                  methods.setValue('headShotUrl', key, {
                    shouldDirty: true,
                    shouldValidate: true
                  })
                }
                onUpload={({ content, contentType }) =>
                  uploadAsset.mutateAsync({
                    data: {
                      content,
                      contentType: contentType as UploadAssetBodyContentType
                    },
                    kind: 'headshots'
                  })
                }
                previewShape='circular'
                previewSrc={headShotUrl ? assetUrl(headShotUrl) : undefined}
                registerCtx={methods.register('headShotUrl')}
                value={headShotUrl}
              />
              <Divider />
              <SwitchInput
                color='success'
                data-testid='advisorIsLocked'
                hideError
                label='Is Locked'
                name='isLocked'
              />
              <Divider />
              <Typography variant='subtitle2'>Permissions</Typography>
              <Grid container spacing={1}>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <CheckboxInput
                    data-testid='advisorHasAllPlans'
                    hideError
                    name='hasAllPlans'
                    option='Has All Plans'
                  />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <CheckboxInput
                    data-testid='advisorCanCreateEmailCampaigns'
                    hideError
                    name='canCreateEmailCampaigns'
                    option='Can Create Email Campaigns'
                  />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <CheckboxInput
                    data-testid='advisorCanEditEmailCampaigns'
                    hideError
                    name='canEditEmailCampaigns'
                    option='Can Edit Email Campaigns'
                  />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <CheckboxInput
                    data-testid='advisorCanRunReports'
                    hideError
                    name='canRunReports'
                    option='Can Run Reports'
                  />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <CheckboxInput
                    data-testid='advisorCanViewSensitiveData'
                    hideError
                    name='canViewSensitiveData'
                    option='Can View Sensitive Data (requires 2FA)'
                  />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <CheckboxInput
                    data-testid='advisorCanIraProspect'
                    hideError
                    name='canIraProspect'
                    option='Can IRA Prospect'
                  />
                </Grid>
                <Grid size={{ sm: 6, xs: 12 }}>
                  <CheckboxInput
                    data-testid='advisorCanInvestorView'
                    hideError
                    name='canInvestorView'
                    option='Can InvestorView'
                  />
                </Grid>
              </Grid>
              {!editingAdvisorId && (
                <>
                  <Divider />
                  <Typography variant='subtitle2'>Login Credentials</Typography>
                  <Input
                    data-testid='advisorUsername'
                    format='string'
                    fullWidth
                    hideError
                    label='Username'
                    name='username'
                    required
                    size='small'
                  />
                  <Input
                    data-testid='advisorPassword'
                    format='password'
                    fullWidth
                    hideError
                    label='Password'
                    name='password'
                    required
                    size='small'
                  />
                </>
              )}
              <CheckboxInput
                data-testid='advisorIsAdmin'
                hideError
                name='isAdmin'
                option='Is Admin'
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid='cancelAdvisor'
              onClick={onCloseDialog}
              variant='outlined'>
              Cancel
            </Button>
            <FormButton
              data-testid='saveAdvisor'
              disabled={uploadAsset.isPending}
              triggerValidation
              variant='contained'>
              Save
            </FormButton>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

AdvisorsTab.displayName = 'AdvisorsTab';
